import { useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';

interface AuditEvent {
  action: string;
  entity_type?: string;
  entity_id?: string;
  details?: Record<string, any>;
}

class AdminAuditLogger {
  private static instance: AdminAuditLogger;
  
  static getInstance(): AdminAuditLogger {
    if (!AdminAuditLogger.instance) {
      AdminAuditLogger.instance = new AdminAuditLogger();
    }
    return AdminAuditLogger.instance;
  }

  async logAction(event: AuditEvent) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Log to Supabase audit table (if exists) or console in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[ADMIN AUDIT]', {
          timestamp: new Date().toISOString(),
          user_id: user.id,
          ...event
        });
      }

      // In production, you could send to external audit service
      // or store in a dedicated audit table
    } catch (error) {
      console.error('Failed to log admin action:', error);
    }
  }
}

export const useAdminAudit = () => {
  const logger = AdminAuditLogger.getInstance();
  
  return {
    logAction: logger.logAction.bind(logger),
    logLogin: () => logger.logAction({ action: 'admin_login' }),
    logLogout: () => logger.logAction({ action: 'admin_logout' }),
    logDataAccess: (entity_type: string, entity_id?: string) => 
      logger.logAction({ action: 'data_access', entity_type, entity_id }),
    logDataModification: (entity_type: string, entity_id: string, details: Record<string, any>) =>
      logger.logAction({ action: 'data_modification', entity_type, entity_id, details })
  };
};

export const AdminAuditLoggerComponent: React.FC = () => {
  const { isAdmin, user } = useAdminAuth();
  const { logLogin, logLogout } = useAdminAudit();

  useEffect(() => {
    if (isAdmin && user) {
      logLogin();
    }
    
    return () => {
      if (isAdmin) {
        logLogout();
      }
    };
  }, [isAdmin, user, logLogin, logLogout]);

  return null;
};